import React, { useMemo } from 'react';
import styled, { ThemeProvider } from 'styled-components';
import { redTheme } from 'src/styles/theme';
import { lineClamp } from 'src/styles/common';
import { useTranslate } from 'react-t7e';
import { motion } from 'framer-motion';
import { media } from 'src/styles/designs';
import { DirectusImage } from 'src/components/common/DirectusImage';
import { Markdown } from 'vendor/mods/markdown/components/Markdown';
import { FormatDate } from 'src/components/common/FormatDate';
import { Events } from 'src/graphql/schema.graphql';
import {
  EventCardVideosLabel,
  EventsGridGalleryItemVideos,
} from 'src/modules/events/components/EventsGridGallery/EventsGridGalleryItemVideos';
import { TrackedLink } from 'src/components/TrackedLink';
import { route } from 'src/services/route';
import { transparentize } from 'polished';
import { useEventRelatedVideosListQuery } from 'src/modules/events/hooks/useEventRelatedVideosListQuery';
import { useFlattenEvent } from 'src/modules/events/hooks/useFlattenEvent';
import { isTimeCompleted } from 'src/hooks/useTimeState';

const Container = styled(motion.li)`
  position: relative;
  width: 100%;
  padding-left: 18px;
  padding-right: 18px;

  ${media.w.greaterThanOrEqual.desktop} {
    width: 300px;
  }
`;

const Card = styled.div<{ $background?: string }>`
  width: calc(100% + 36px);
  margin-left: -18px;
  margin-right: -18px;
  padding: 18px;

  position: relative;
  color: ${({ theme }) => theme.text.palette.primary};
  background: ${({ theme, $background }) =>
    $background ?? theme.palette.accent};

  margin-bottom: 16px;
  height: 200px;

  ${media.w.greaterThanOrEqual.desktop} {
    margin-bottom: 18px;
    height: 324px;
  }
`;

const BackgroundImage = styled(DirectusImage)`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: center top;

  ${media.w.greaterThanOrEqual.desktop} {
    object-position: center center;
  }
`;

const CardContent = styled.div`
  height: 100%;
  position: relative;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
`;

const Date = styled.div`
  font-style: normal;
  font-weight: 300;
  font-size: 16px;
  line-height: 150%;
  white-space: nowrap;
  margin-right: 16px;
`;

const Name = styled.h2`
  font-size: 32px;
  font-weight: 300;
  line-height: 1;
  letter-spacing: 0.64rpx;
  text-transform: uppercase;
`;

const Title = styled.h3`
  font-size: 32px;
  font-weight: 200;
  line-height: 1;
  letter-spacing: 0.64rpx;
  text-transform: uppercase;
  max-width: 100%;
  word-wrap: break-word;
  ${lineClamp(3)};
`;

const Description = styled.p`
  font-style: normal;
  font-weight: 300;
  font-size: 14px;
  line-height: 1.5;

  ${lineClamp(3)};
`;

const Tags = styled.p`
  margin: 18px 0 0;

  font-style: normal;
  font-weight: 300;
  font-size: 14px;
  line-height: 1.5;

  ${lineClamp(2)};

  color: ${({ theme }) => theme.text.palette.secondary};
`;

const TagsTitle = styled.span`
  text-transform: uppercase;
  letter-spacing: 0.64rpx;
`;

const CardTop = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const CardLink = styled.a`
  &:hover {
    ${EventCardVideosLabel} {
      background: ${({ theme }) =>
        transparentize(0.5, theme.palette.background)};
      color: ${({ theme }) => theme.text.palette.accent};
      backdrop-filter: blur(16rpx);
    }
  }
`;

const animationConfig = {
  hide: { opacity: 0, y: 10 },
  show: { opacity: 1, y: 0 },
};

export interface EventsGridGalleryItemProps {
  event: Events;
}

export const EventsGridGalleryItem = ({
  event,
}: EventsGridGalleryItemProps) => {
  const { _ } = useTranslate();

  const { data: relatedVideos } = useEventRelatedVideosListQuery({ event });
  const flatEvent = useFlattenEvent(event);

  const link = useMemo(() => {
    if (relatedVideos?.length === 1) {
      return route.video(relatedVideos[0]).page();
    }
    if (Number(relatedVideos?.length) > 1) {
      return route.event(event).episodes();
    }

    if (isTimeCompleted(event)) {
      return flatEvent.pastCtaLink;
    }

    return route.event(event).page();
  }, [relatedVideos, event, flatEvent]);

  const content = useMemo(
    () => (
      <>
        <ThemeProvider theme={redTheme}>
          <Card $background={flatEvent.background}>
            {flatEvent.image != null && (
              <BackgroundImage src={flatEvent.image.id} layout="fill" />
            )}
            <CardContent>
              <CardTop>
                <Date>
                  <FormatDate format="dd.LL.yyyy">
                    {flatEvent.startDate}
                  </FormatDate>
                </Date>
                <EventsGridGalleryItemVideos relatedVideos={relatedVideos} />
              </CardTop>
              <div>
                {flatEvent.type?.name != null && (
                  <>
                    <Name>{flatEvent.type?.name}</Name>
                    {flatEvent.name && <Title>{flatEvent.name}</Title>}
                  </>
                )}
                {flatEvent.type?.name == null && <Name>{flatEvent.name}</Name>}
              </div>
            </CardContent>
          </Card>
        </ThemeProvider>
        <Description>
          <Markdown>{flatEvent.description}</Markdown>
        </Description>
        {flatEvent.tags.length > 0 && (
          <Tags>
            <TagsTitle>{_('EVENT TOPICS')}: </TagsTitle>
            {flatEvent.tags.map((a) => a.name).join(', ')}
          </Tags>
        )}
      </>
    ),
    [flatEvent, relatedVideos, _],
  );

  return (
    <Container variants={animationConfig}>
      {link ? (
        <TrackedLink href={link} passHref>
          <CardLink>{content}</CardLink>
        </TrackedLink>
      ) : (
        content
      )}
    </Container>
  );
};
